import { LoginResult, MfaMethod } from '@vivport/backend/types';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authKeys } from '@/hooks/useAuthQueries';
import { completeLogin } from '@/utils/auth-utils';
import { LoginMfaEmailCode } from './LoginMfaEmailCode';
import { LoginMfaMethodSelector } from './LoginMfaMethodSelector';
import { LoginMfaPasskey } from './LoginMfaPasskey';

interface LoginMfaProps {
  mfaToken: string;
  mfaMethods: MfaMethod[];
  onBack: () => void;
}

export function LoginMfa({ mfaToken, mfaMethods, onBack }: LoginMfaProps) {
  const [selectedMethod, setSelectedMethod] = useState<MfaMethod | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleMfaSuccess = async (result: LoginResult) => {
    await queryClient.cancelQueries({ queryKey: authKeys.user() });
    const authData = completeLogin(result);
    queryClient.setQueryData(authKeys.user(), authData);
    queryClient.invalidateQueries({ queryKey: authKeys.user() });
    navigate('/explore');
  };

  const handleMfaError = (errorMessage: string) => {
    // Error handling is done by individual components via toast
    console.error('MFA Error:', errorMessage);
  };

  const handleBackToSelector = () => {
    setSelectedMethod(null);
  };

  return (
    <>
      {!selectedMethod && (
        <LoginMfaMethodSelector
          mfaMethods={mfaMethods}
          onMethodSelect={setSelectedMethod}
          onBack={onBack}
        />
      )}

      {selectedMethod === 'EMAIL' && (
        <LoginMfaEmailCode
          mfaToken={mfaToken}
          onSuccess={handleMfaSuccess}
          onError={handleMfaError}
          onBack={handleBackToSelector}
        />
      )}

      {selectedMethod === 'FIDO2' && (
        <LoginMfaPasskey
          mfaToken={mfaToken}
          onSuccess={handleMfaSuccess}
          onError={handleMfaError}
          onBack={handleBackToSelector}
          autoTrigger={true}
        />
      )}
    </>
  );
}

